import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useRef } from 'react';
import { orderBy, noop } from 'lodash-es';
import { Page, TableList, PageEmptyState, PageErrorState, Button, PageContentHeader, FilterPickerGroup } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { AddToAnotherClientModal } from '~/components/AddToAnotherClientModal/AddToAnotherClientModal';
import { EditContactModal } from '~/components/EditContactModal/EditContactModal';
import { EmptyStateIllustration } from '~/components/EmptyStateIllustration/EmptyStateIllustration';
import TableFilters from '~/components/TableFilters/TableFilters';
import { SearchFilterType } from '~/components/TableFilters/types';
import { useEventReporter, useOverlayContext, useStore, useStoreActions } from '~/contexts';
import { useAccountingCustomers } from '~/hooks/reactQuery/queries/useAccountingCustomers';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useIntegrationsSummary } from '~/hooks/reactQuery/queries/useIntegrationsSummary';
import { useContactArchiveRestoreDelete } from '~/hooks/useContactArchiveRestoreDelete';
import { useDebouncedLogSearchToBI } from '~/hooks/useDebouncedLogSearchToBI';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useStateFromUrl } from '~/hooks/useStateFromUrl';
import { useLocaleKeys } from '~/i18n';
import { ContactStatus } from '~/models/ContactModel';
import { selectors } from '~/store/state-selectors';
import { Accounting } from '~/types/accounting';
import { CreateProposalButton } from '../../components/CreateProposalButton/CreateProposalButton';
import PageLoadingState from '../../components/PageLoadingState';
import { contactBiData } from './ContactsDashboardOld.utils';
import ContactsDashboardRowOld from './ContactsDashboardRowOld';
const sortIds = [
    'companyName',
    'status',
    'primaryContactName',
    'contactDetails'
];
const columnDefs = [
    {
        width: [
            140,
            300
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_contacts_by_company_name'
        }
    },
    {
        width: [
            140,
            300
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_contacts_by_status'
        }
    },
    {
        width: [
            170,
            170
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_contacts_by_primary_contact_name'
        }
    },
    {
        width: [
            160,
            300
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_contacts_by_contact_details'
        }
    }
];
const singleContactBiEventIds = {
    delete: {
        rowMenuClick: 11618,
        confirm: 11620,
        back: 11623,
        close: 11624,
        success: 11626
    }
};
export const GetContactsDashboardOldTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            ContactsDashboardOldTestIds: {
                Root: 'contacts-dashboard',
                EmptyStatePage: 'empty-state-page',
                ErrorStatePage: 'error-state-page',
                EmptyStateNewButton: 'contacts-empty-state-new-btn',
                Search: 'contacts-dashboard-search',
                TableHeader: 'contacts-dashboard-table-header',
                EmptySearchClearButton: 'contacts-dashboard-empty-search-clear-btn'
            }
        }
    });
export function ActualContactsDashboardOld(props) {
    const { data: contacts, isRefreshing, refresh } = props;
    const { ContactsDashboardOldTestIds } = useMemo(()=>GetContactsDashboardOldTID(), []);
    const { logBI } = useStoreActions();
    const { reportToGtm, reportToAppcues } = useEventReporter();
    const { showModal } = useOverlayContext();
    const defaultSort = useMemo(()=>({
            colIndex: 0,
            sort: 'desc'
        }), []);
    const { sort: tableSort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const scrollElRef = useRef(null);
    const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11602);
    const { urlState: { search, status: contactStatusFilterValue }, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        },
        status: {
            defaultValue: [
                ContactStatus.Active
            ],
            multi: true
        }
    });
    const hasContacts = !!(contacts && contacts.length);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    useIntegrationsSummary({
        enabled: showQboIntegration || showXeroIntegration,
        refreshOnHookMount: true
    });
    const { customers: qboCustomers } = useAccountingCustomers(Accounting.QBO, {
        enabled: showQboIntegration,
        refreshOnHookMount: true
    });
    const { customers: xeroCustomers } = useAccountingCustomers(Accounting.Xero, {
        enabled: showXeroIntegration,
        refreshOnHookMount: true
    });
    const [registerSearchBoxHotkey, setRegisterSearchBoxHotkey] = useState(true);
    const { t: contactsDashboardTexts } = useLocaleKeys('client.contactsDashboardOld');
    const { t: columnHeaderTexts } = useLocaleKeys('client.contactsDashboardOld.columnHeaders');
    const { t: columnsTexts } = useLocaleKeys('client.contactsDashboardOld.columns');
    const { t: statusFilterTexts } = useLocaleKeys('client.contactsDashboardOld.contactStatusFilter');
    const sortOptions = useMemo(()=>[
            {
                id: 'companyName',
                label: columnsTexts.clientName(),
                defaultDirection: 'desc',
                colIndex: 0
            },
            {
                id: 'status',
                label: columnsTexts.status(),
                defaultDirection: 'desc',
                colIndex: 1
            },
            {
                id: 'primaryContactName',
                label: columnsTexts.contactName(),
                defaultDirection: 'desc',
                colIndex: 2
            },
            {
                id: 'contactDetails',
                label: columnsTexts.contactDetails(),
                defaultDirection: 'desc',
                colIndex: 3
            }
        ], []);
    const { t: emptyStateTexts } = useLocaleKeys('client.contactsDashboardOld.emptyState');
    const headerItems = useMemo(()=>[
            {
                id: 'companyName',
                value: columnHeaderTexts.clientName()
            },
            {
                id: 'status',
                value: columnHeaderTexts.status()
            },
            {
                id: 'primaryContactName',
                value: columnHeaderTexts.contactName()
            },
            {
                id: 'contactDetails',
                value: columnHeaderTexts.contactDetails()
            }
        ], []);
    const contactStatusFilterOptions = useMemo(()=>[
            {
                value: ContactStatus.Active,
                label: statusFilterTexts.active()
            },
            {
                value: ContactStatus.Archived,
                label: statusFilterTexts.archived()
            }
        ], []);
    const filterLabels = useMemo(()=>({
            status: statusFilterTexts.label()
        }), []);
    const filterConfig = useMemo(()=>({
            status: {
                type: SearchFilterType.MultiSelectFilter,
                removable: false,
                options: contactStatusFilterOptions,
                mobile: {
                    isEmptyValue: (value)=>{
                        const statuses = value;
                        return !Array.isArray(statuses) || statuses.length === 0 || statuses.length === contactStatusFilterOptions.length;
                    }
                }
            }
        }), [
        contactStatusFilterOptions
    ]);
    const selectedFilters = useMemo(()=>({
            status: contactStatusFilterValue
        }), [
        contactStatusFilterValue
    ]);
    const handleTableFiltersRefresh = (filters, sort)=>{
        const allStatuses = contactStatusFilterOptions.map((opt)=>opt.value);
        const selectedStatus = filters.status;
        const newStatus = (selectedStatus === null || selectedStatus === void 0 ? void 0 : selectedStatus.length) ? selectedStatus : allStatuses;
        changeUrlState({
            status: newStatus
        });
        if (sort !== undefined) {
            changeSort(sort);
        }
    };
    const qboCustomerIdToDisplay = useMemo(()=>{
        if (!qboCustomers) return {};
        return qboCustomers.reduce((prev, qboCustomer)=>{
            prev[qboCustomer.id()] = qboCustomer.formattedDisplay();
            return prev;
        }, {});
    }, [
        qboCustomers
    ]);
    const xeroCustomerIdToDisplay = useMemo(()=>{
        if (!xeroCustomers) return {};
        return xeroCustomers.reduce((prev, xeroCustomer)=>{
            prev[xeroCustomer.id()] = xeroCustomer.formattedDisplay();
            return prev;
        }, {});
    }, [
        xeroCustomers
    ]);
    const filteredAndSorted = useMemo(()=>{
        const filtered = (contacts || []).filter((contact)=>{
            // search
            const trimmedSearch = search ? search.trim().toLowerCase() : '';
            const termForSearch = "".concat(contact.companyName(), " ").concat(contact.firstName(), " ").concat(contact.lastName(), " ").concat(contact.email());
            const passesSearch = termForSearch.toLowerCase().includes(trimmedSearch);
            // status filter
            const passesStatusFilter = contactStatusFilterValue.includes(contact.status());
            return passesSearch && passesStatusFilter;
        });
        const { sort, colIndex } = tableSort || {
            colIndex: 0,
            sort: 'asc'
        };
        const sortPropertiesByHeaderIndex = [
            [
                (c)=>c.companyName()
            ],
            [
                (s)=>s.status(),
                (c)=>c.companyName()
            ],
            [
                (c)=>c.firstName(),
                (c)=>c.lastName(),
                (c)=>c.companyName()
            ],
            [
                (c)=>c.email(),
                (c)=>c.companyName()
            ]
        ];
        const sortProperties = sortPropertiesByHeaderIndex[colIndex];
        const sortingOrder = sort === 'asc' ? 'desc' : 'asc';
        const orders = Array.from(Array(sortProperties.length)).map(()=>sortingOrder);
        return orderBy(filtered, sortProperties, orders);
    }, [
        contacts,
        tableSort,
        search,
        contactStatusFilterValue
    ]);
    const resetKey = JSON.stringify({
        search,
        contactStatusFilterValue,
        tableSort
    });
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: filteredAndSorted,
        scrollElRef,
        resetKey
    });
    const onSortChange = (sort)=>{
        logBI(11603, {
            column: sort ? headerItems[sort.colIndex].id : 'none'
        });
        changeSort(sort);
    };
    const onClearSearch = ()=>{
        changeUrlState({
            search: ''
        });
        debouncedLogSearchToBI.onClear();
    };
    function onClearFilters() {
        changeUrlState({
            search: '',
            status: contactStatusFilterOptions.map((opt)=>opt.value)
        });
    }
    const openContactModal = async (contact)=>{
        logBI(11600, {
            contact: contact.toJSON()
        });
        reportToGtm('button-click', 'contacts-table-view-contact');
        reportToAppcues('button-click', 'contacts-table-view-contact');
        const clientId = contact.customerId();
        if (clientId == null) return;
        setRegisterSearchBoxHotkey(false);
        try {
            await showModal({
                biName: 'edit_contact_modal',
                closeOnEscapeKey: true,
                closeOnClickOutside: true,
                content: (param)=>{
                    let { dismissModal } = param;
                    return /*#__PURE__*/ _jsx(EditContactModal, {
                        clientId: clientId,
                        contact: contact,
                        lockedClientName: contact.companyName(),
                        dismissModal: dismissModal
                    });
                }
            });
            await refresh();
        } finally{
            setRegisterSearchBoxHotkey(true);
        }
    };
    const openAddToAnotherClientModal = async (contact)=>{
        const renderAddToAnotherClientModal = (dismissModal)=>/*#__PURE__*/ _jsx(AddToAnotherClientModal, {
                contact: contact,
                contacts: contacts !== null && contacts !== void 0 ? contacts : [],
                dismissModal: dismissModal
            });
        setRegisterSearchBoxHotkey(false);
        try {
            await showModal({
                biName: 'add_to_another_client_modal',
                closeOnEscapeKey: true,
                closeOnClickOutside: true,
                content: (param)=>{
                    let { dismissModal } = param;
                    return renderAddToAnotherClientModal(dismissModal);
                }
            });
            await refresh();
        } finally{
            setRegisterSearchBoxHotkey(true);
        }
    };
    const { onDelete: onDeleteContact } = useContactArchiveRestoreDelete({
        onSuccess: refresh,
        bi: {
            delete: {
                onMenuClick: (contact)=>logBI(singleContactBiEventIds.delete.rowMenuClick, contactBiData(contact)),
                onBack: (contact)=>logBI(singleContactBiEventIds.delete.back, contactBiData(contact)),
                onClose: (contact)=>logBI(singleContactBiEventIds.delete.close, contactBiData(contact)),
                onConfirm: (contact)=>logBI(singleContactBiEventIds.delete.confirm, contactBiData(contact)),
                onSuccess: (contact)=>logBI(singleContactBiEventIds.delete.success, contactBiData(contact))
            }
        }
    });
    const renderEmptyPage = ()=>/*#__PURE__*/ _jsx(Page, {
            testId: ContactsDashboardOldTestIds.Root(),
            header: contactsDashboardTexts.title(),
            loading: isRefreshing,
            onRefresh: refresh,
            variant: "containedHeight",
            children: /*#__PURE__*/ _jsx(PageEmptyState, {
                maxHeight: true,
                testId: ContactsDashboardOldTestIds.EmptyStatePage(),
                illustration: /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
                message: emptyStateTexts.noContactsMessage(),
                actions: /*#__PURE__*/ _jsx(CreateProposalButton, {
                    testId: ContactsDashboardOldTestIds.EmptyStateNewButton(),
                    text: emptyStateTexts.createAgreement()
                })
            })
        });
    const renderContactsPage = ()=>/*#__PURE__*/ _jsx(Page, {
            testId: ContactsDashboardOldTestIds.Root(),
            ref: scrollElRef,
            onRefresh: refresh,
            header: contactsDashboardTexts.title(),
            subheader: contactsDashboardTexts.subheader(),
            loading: isRefreshing,
            onScroll: onScroll,
            children: /*#__PURE__*/ _jsxs(TableList.Provider, {
                hasActionColumn: true,
                columnDefs: columnDefs,
                sort: tableSort,
                onSortChange: onSortChange,
                children: [
                    /*#__PURE__*/ _jsxs(PageContentHeader, {
                        children: [
                            /*#__PURE__*/ _jsx(FilterPickerGroup, {
                                style: {
                                    marginBottom: 24
                                },
                                children: /*#__PURE__*/ _jsx(TableFilters, {
                                    testIdPrefix: ContactsDashboardOldTestIds.Root(),
                                    filterLabels: filterLabels,
                                    filterConfig: filterConfig,
                                    selectedFilters: selectedFilters,
                                    setSelectedFilters: noop,
                                    onRefresh: handleTableFiltersRefresh,
                                    search: {
                                        bi: {
                                            name: 'contacts_search'
                                        },
                                        value: search,
                                        testId: ContactsDashboardOldTestIds.Search(),
                                        registerHotkey: registerSearchBoxHotkey,
                                        onChange: (event)=>{
                                            const searchStr = event.target.value;
                                            changeUrlState({
                                                search: searchStr
                                            });
                                            debouncedLogSearchToBI.onChange(searchStr);
                                        },
                                        onClear: onClearSearch
                                    },
                                    mobile: {
                                        sortOptions
                                    }
                                })
                            }),
                            /*#__PURE__*/ _jsx(TableList.Header, {
                                items: headerItems,
                                testId: ContactsDashboardOldTestIds.TableHeader()
                            })
                        ]
                    }),
                    filteredAndSorted.length ? paginatedData.map((contact)=>/*#__PURE__*/ _jsx(ContactsDashboardRowOld, {
                            contact: contact,
                            isQboIntegrated: showQboIntegration,
                            isXeroIntegrated: showXeroIntegration,
                            qboCustomerIdToDisplay: qboCustomerIdToDisplay,
                            xeroCustomerIdToDisplay: xeroCustomerIdToDisplay,
                            openContactModal: openContactModal,
                            onDeleteContact: onDeleteContact,
                            onAddToAnotherClient: openAddToAnotherClientModal
                        }, contact.id())) : /*#__PURE__*/ _jsx(PageEmptyState, {
                        illustration: /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
                        title: emptyStateTexts.noMatchTitle(),
                        message: emptyStateTexts.noMatchMessage(),
                        actions: /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'clear_contacts_filters'
                            },
                            testId: ContactsDashboardOldTestIds.EmptySearchClearButton(),
                            onClick: onClearFilters,
                            children: emptyStateTexts.clearFilters()
                        })
                    })
                ]
            })
        });
    return hasContacts ? renderContactsPage() : renderEmptyPage();
}
export function ContactsDashboardOld() {
    const { contacts, isLoading, isFetching, refresh, error } = useContacts({
        refreshOnMount: true
    });
    const { ContactsDashboardOldTestIds } = useMemo(()=>GetContactsDashboardOldTID(), []);
    const { t: contactsTexts } = useLocaleKeys('client.contactsDashboardOld');
    useSetDocumentTitle(contactsTexts.title());
    const handleRefresh = async ()=>{
        const result = await refresh();
        return result.data;
    };
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoadingState, {
            header: contactsTexts.title()
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            testId: ContactsDashboardOldTestIds.ErrorStatePage(),
            title: contactsTexts.title(),
            onRefresh: handleRefresh,
            pullToRefresh: true
        });
    }
    return /*#__PURE__*/ _jsx(ActualContactsDashboardOld, {
        data: contacts,
        isRefreshing: isFetching,
        refresh: handleRefresh
    });
}
